import { Suspense, lazy } from 'react';
import { useMachine } from './state/machine.ts';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { Landing } from './routes/Landing/Landing.tsx';
import { LegalGate } from './routes/LegalGate/LegalGate.tsx';
import { Permission } from './routes/Permission/Permission.tsx';
import { Calibration } from './routes/Calibration/Calibration.tsx';
import { useUi } from './state/ui.store.ts';
import { useEdgeHealth } from './hooks/useEdgeHealth.ts';

const Queue = lazy(() => import('./routes/Queue/Queue.tsx').then((m) => ({ default: m.Queue })));
const Duel = lazy(() => import('./routes/Duel/Duel.tsx').then((m) => ({ default: m.Duel })));
const Resolution = lazy(() =>
  import('./routes/Resolution/Resolution.tsx').then((m) => ({ default: m.Resolution })),
);
const Share = lazy(() => import('./routes/Share/Share.tsx').then((m) => ({ default: m.Share })));
const SoloScan = lazy(() =>
  import('./routes/SoloScan/SoloScan.tsx').then((m) => ({ default: m.SoloScan })),
);
const SoloSmileScan = lazy(() =>
  import('./routes/SoloSmileScan/SoloSmileScan.tsx').then((m) => ({ default: m.SoloSmileScan })),
);
const SoloFrownScan = lazy(() =>
  import('./routes/SoloFrownScan/SoloFrownScan.tsx').then((m) => ({ default: m.SoloFrownScan })),
);
const SoloAngerScan = lazy(() =>
  import('./routes/SoloAngerScan/SoloAngerScan.tsx').then((m) => ({ default: m.SoloAngerScan })),
);
const SoloSurpriseScan = lazy(() =>
  import('./routes/SoloSurpriseScan/SoloSurpriseScan.tsx').then((m) => ({ default: m.SoloSurpriseScan })),
);
const SoloWinkScan = lazy(() =>
  import('./routes/SoloWinkScan/SoloWinkScan.tsx').then((m) => ({ default: m.SoloWinkScan })),
);
const SoloDisgustScan = lazy(() =>
  import('./routes/SoloDisgustScan/SoloDisgustScan.tsx').then((m) => ({ default: m.SoloDisgustScan })),
);
const SoloRestingFaceScan = lazy(() =>
  import('./routes/SoloRestingFaceScan/SoloRestingFaceScan.tsx').then((m) => ({ default: m.SoloRestingFaceScan })),
);
const SoloStillnessChallenge = lazy(() =>
  import('./routes/SoloStillnessChallenge/SoloStillnessChallenge.tsx').then((m) => ({ default: m.SoloStillnessChallenge })),
);
const SoloNodChallenge = lazy(() =>
  import('./routes/SoloNodChallenge/SoloNodChallenge.tsx').then((m) => ({ default: m.SoloNodChallenge })),
);
const SoloShakeChallenge = lazy(() =>
  import('./routes/SoloShakeChallenge/SoloShakeChallenge.tsx').then((m) => ({ default: m.SoloShakeChallenge })),
);
const SoloBlinkChallenge = lazy(() =>
  import('./routes/SoloBlinkChallenge/SoloBlinkChallenge.tsx').then((m) => ({ default: m.SoloBlinkChallenge })),
);

function Toasts() {
  const { toasts, dismiss } = useUi();
  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        display: 'grid',
        gap: 8,
        zIndex: 9999,
        maxWidth: 360,
      }}
    >
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => dismiss(t.id)}
          style={{
            background: 'var(--c-panel)',
            border: `1px solid ${t.kind === 'error' ? 'var(--c-blood)' : 'var(--c-stroke)'}`,
            color: 'var(--c-fg)',
            padding: '12px 14px',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--f-mono)',
            fontSize: 13,
            textAlign: 'left',
            boxShadow: 'var(--shadow-soft)',
          }}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}

export function App() {
  const state = useMachine((m) => m.state);
  useEdgeHealth();

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        {state === 'landing' && <Landing />}
        {state === 'legal-gate' && <LegalGate />}
        {(state === 'permission' || state === 'permission-denied') && <Permission />}
        {state === 'calibration' && <Calibration />}
        {(state === 'queue' || state === 'queue-timeout') && <Queue />}
        {(state === 'connecting' ||
          state === 'dueling' ||
          state === 'resolving' ||
          state === 'peer-dropped' ||
          state === 'report-flow') && <Duel />}
        {state === 'resolution' && <Resolution />}
        {state === 'share' && <Share />}
        {state === 'solo-scan' && <SoloScan />}
        {state === 'solo-smile-scan' && <SoloSmileScan />}
        {state === 'solo-frown-scan' && <SoloFrownScan />}
        {state === 'solo-anger-scan' && <SoloAngerScan />}
        {state === 'solo-surprise-scan' && <SoloSurpriseScan />}
        {state === 'solo-wink-scan' && <SoloWinkScan />}
        {state === 'solo-disgust-scan' && <SoloDisgustScan />}
        {state === 'solo-resting-face-scan' && <SoloRestingFaceScan />}
        {state === 'solo-stillness-challenge' && <SoloStillnessChallenge />}
        {state === 'solo-nod-challenge' && <SoloNodChallenge />}
        {state === 'solo-shake-challenge' && <SoloShakeChallenge />}
        {state === 'solo-blink-challenge' && <SoloBlinkChallenge />}
      </Suspense>
      <Toasts />
    </ErrorBoundary>
  );
}
